import { useEffect, useState } from "react";
import Header from "../../../components/Header";
import TabNavbar from "../../../components/Tabbar";
import { HR_TABS } from "../../../utils/TabbarData";
import SearchWithFilter from "../../../components/SearchWithFilter";
import { RootState, useAppDispatch } from "../../../redux/store";
import {
  fetchAdditionalFields,
  getCourses,
  updateCourseCost,
} from "../../../redux/slices/couses.slice";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import SpinnerLoader from "../../../components/SpinnerLoader";
import HomeButton from "../../../components/HomeButton";

const OPERATOR_MAP: any = {
  "=": "eq",
  ">": "gt",
  "<": "lt",
  ">=": "gte",
  "<=": "lte",
};
const HRTrainingCost = () => {
  const dispatch = useAppDispatch();
  const [searchTerm, setSearchTerm] = useState("");
  const [itemsPerPage, setItemsPerPage] = useState(4);
  const [currentPage, setCurrentPage] = useState(1); // Current page state
  const [editableFields, setEditableFields] = useState<{
    [key: string]: boolean;
  }>({});
  const [newCosts, setNewCosts] = useState<{
    [key: string]: { classroom: any; online: any };
  }>({});
  const [typeFilter, setTypeFilter] = useState(""); // Local / Group or empty
  const [languageFilter, setLanguageFilter] = useState(""); // string input
  const [minJobLevelFilter, setMinJobLevelFilter] = useState(""); // number input as string
  const [minJobLevelOperator, setMinJobLevelOperator] = useState("=");
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [debouncedLanguageFilter, setDebouncedLanguageFilter] =
    useState(languageFilter);
  const [debouncedMinJobLevelFilter, setDebouncedMinJobLevelFilter] =
    useState(minJobLevelFilter);
  const [minJobLevelError, setMinJobLevelError] = useState("");
  const { t } = useTranslation();

  const { courseResp, courses, loading }: any = useSelector(
    (state: RootState) => state.course
  );

  // Update debouncedLanguageFilter 500ms after user stops typing
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedLanguageFilter(languageFilter);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [languageFilter]);

  // Update debouncedMinJobLevelFilter 500ms after user stops typing
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedMinJobLevelFilter(minJobLevelFilter);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [minJobLevelFilter]);

  // Handle page change

  const handlePageChange = async (page: number) => {
    const { names, operators, values } = buildFilterParams();

    const params: any = {
      page_size: itemsPerPage,
      page,
      filter_value: searchTerm || "",
      role: "HR",
      hr_tab: "training_cost",
      user_id: sessionStorage.getItem("userId"),
    };

    if (names.length > 0) {
      params.additional_filter_name = names.join(",");
      params.additional_filter_operator = operators.join(",");
      params.additional_filter_value = values.join(",");
    }

    await dispatch(getCourses(params));
    setCurrentPage(page);
  };

  const buildFilterParams = () => {
    const names: string[] = [];
    const operators: string[] = [];
    const values: string[] = [];

    if (typeFilter) {
      names.push("category_type");
      operators.push("equals");
      values.push(typeFilter.toUpperCase());
    }
    if (languageFilter) {
      names.push("language");
      operators.push("equals"); // or "contains" if API supports
      values.push(debouncedLanguageFilter.toLowerCase());
    }
    if (minJobLevelFilter) {
      names.push("minimum_job_level");
      operators.push(OPERATOR_MAP[minJobLevelOperator]);
      values.push(debouncedMinJobLevelFilter);
    }

    return { names, operators, values };
  };

  const applyFilters = async () => {
    const { names, operators, values } = buildFilterParams();
    // Prepare params object for API call
    const params: any = {
      page_size: itemsPerPage,
      page: 1,
      filter_value: searchTerm || "",
      role: "HR",
      hr_tab: "training_cost",
      user_id: sessionStorage.getItem("userId"),
    };

    // Add filter params only if any filters applied
    if (names.length > 0) {
      params.additional_filter_name = names.join(",");
      params.additional_filter_operator = operators.join(",");
      params.additional_filter_value = values.join(",");
    }

    await dispatch(getCourses(params));
    setCurrentPage(1);

    setCurrentPage(1);
  };

  // Handle search input
  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    setCurrentPage(1);
  };

  useEffect(() => {
    applyFilters();
  }, [
    searchTerm,
    typeFilter,
    debouncedLanguageFilter,
    debouncedMinJobLevelFilter,
    minJobLevelOperator,
    itemsPerPage,
  ]);

  const fetchCourses = async () => {
    await dispatch(
      getCourses({
        page_size: itemsPerPage,
        page: currentPage,
        filter_value: searchTerm, // Pass search term to API
        role: "HR",
        hr_tab: "training_cost",
        user_id: sessionStorage.getItem("userId"),
      })
    );
  };

  const handleEdit = (courseId: string) => {
    setEditableFields((prev) => ({ ...prev, [courseId]: true }));
    setNewCosts((prev) => ({
      ...prev,
      [courseId]: {
        classroom:
          courses.find((course: any) => course.course_id === courseId)
            ?.classroom_training_cost || "",
        classroom_id:
          courses.find((course: any) => course.course_id === courseId)
            ?.classroom_training_cost_id || null,
        online:
          courses.find((course: any) => course.course_id === courseId)
            ?.online_training_cost || "",
        online_id:
          courses.find((course: any) => course.course_id === courseId)
            ?.online_training_cost_id || null,
      },
    }));
  };

  const handleSave = async (courseId: string) => {
    const updatedCosts: any = newCosts[courseId];
    const payload = {
      items: [
        {
          id: updatedCosts.classroom_id,
          value: updatedCosts.classroom.toString(),
        },
        { id: updatedCosts.online_id, value: updatedCosts.online.toString() },
      ],
    };
    await dispatch(updateCourseCost({ courseId, updatedCosts: payload }));

    setEditableFields((prev) => ({ ...prev, [courseId]: false }));
    await dispatch(
      getCourses({
        page_size: itemsPerPage,
        page: currentPage,
        filter_value: searchTerm, // Pass search term to API
        role: "HR",
        hr_tab: "training_cost",
        user_id: sessionStorage.getItem("userId"),
      })
    );
  };

  // Dispatch additional field fetch after courses are loaded
  useEffect(() => {
    if (courses.length > 0) {
      courses.forEach((course: any) => {
        dispatch(fetchAdditionalFields(course.course_id));
      });
    }
  }, [courses]);

  const handleInputChange = (
    courseId: any,
    type: "classroom" | "online",
    value: any,
    costId: any
  ) => {
    const numericValue = value.replace(/[^0-9a-zA-Z., ]/g, "");
    setNewCosts((prev) => ({
      ...prev,
      [courseId]: {
        ...prev[courseId],
        [type]: numericValue.toString(),
        [type === "classroom" ? "classroom_id" : "online_id"]: costId,
      },
    }));
  };

  // Rendered data (from API)
  const totalPages = Math.ceil(
    (courseResp?.total_item_count || 0) / itemsPerPage
  );
  const isMobileOrTablet = window.innerWidth < 1024;
  return (
    <div className="flex flex-col items-start gap-8">
      {/* <HomeButton />
      <Header /> */}

      {!isMobileOrTablet && <HomeButton />}
      {isMobileOrTablet ? (
        <>
          {" "}
          <TabNavbar
            id="courses"
            tabs={HR_TABS}
            selectedTabId={5}
            type="Training Cost"
          />
          <HomeButton />
          <Header />
        </>
      ) : (
        <>
          <Header />
          <TabNavbar
            id="courses"
            tabs={HR_TABS}
            selectedTabId={5}
            type="Training Cost"
          />{" "}
        </>
      )}
      {/* <TabNavbar id="seat-allocation" tabs={ADMIN_TABS} selectedTabId={2} /> */}
      <div
        className={`${
          isMobileOrTablet ? "flex flex-col" : "flex flex-row"
        } gap-4 w-full`}
      >
        {/* Search and Filter */}
        <div className="w-full flex justify-between items-center gap-2">
          <SearchWithFilter
            searchTerm={searchTerm}
            onSearchChange={handleSearchChange}
            disabled={loading}
            isMobileOrTablet={isMobileOrTablet}
          />
        </div>

        {/* Filter Widget */}
        {isFilterOpen && (
          <div
            className={`absolute ${
              isMobileOrTablet ? "top-[41%]" : "top-[43%] left-[20%]"
            }  mt-2 bg-white shadow-lg rounded border w-80 p-4 z-50`}
            style={{ minWidth: "300px" }}
          >
            {/* Close button */}
            <button
              className="absolute top-2 right-2 text-gray-600 hover:text-gray-900"
              onClick={() => setIsFilterOpen(false)}
            >
              &#10005;
            </button>

            <div className="flex flex-col gap-2">
              {/* TYPE Dropdown */}
              <label>{t("Type")}</label>
              <select
                className="border p-2 rounded"
                value={typeFilter}
                onChange={(e) => {
                  setTypeFilter(e.target.value);
                  // setLanguageFilter("");
                  // setMinJobLevelFilter("");
                  // setMinJobLevelOperator("=");
                }}
              >
                <option value="">{t("All")}</option>
                <option value="Local">{t("Local")}</option>
                <option value="Group">{t("Group")}</option>
              </select>

              {/* Language Text */}
              <label>{t("Language")}</label>
              <input
                type="text"
                className="border p-2 rounded"
                placeholder={t("Enter language")}
                value={languageFilter}
                onChange={(e) => {
                  setLanguageFilter(e.target.value);
                  // setMinJobLevelFilter("");
                  // setMinJobLevelOperator("=");
                  // setTypeFilter("");
                }}
              />

              {/* Min Career Level with Operator */}
              <label>{t("Min Career Level")}</label>
              <div className="w-full">
                <div className="flex gap-2">
                  <select
                    className="border p-2 rounded"
                    value={minJobLevelOperator}
                    onChange={(e) => {
                      setMinJobLevelOperator(e.target.value);
                      // setTypeFilter("");
                      // setLanguageFilter("");
                    }}
                  >
                    <option value="=">{"="}</option>
                    <option value=">">{">"}</option>
                    <option value="<">{"<"}</option>
                    <option value=">=">{">="}</option>
                    <option value="<=">{"<="}</option>
                  </select>
                  <input
                    style={{ width: "120px" }}
                    type="number"
                    className="border p-2 rounded"
                    placeholder={t("Enter level")}
                    value={minJobLevelFilter}
                    onChange={(e) => {
                      // setMinJobLevelFilter(e.target.value);
                      const val = e.target.value;

                      // Validation
                      const numVal = Number(val);
                      if (val === "") {
                        setMinJobLevelError(""); // no error if empty
                        setMinJobLevelFilter(val);
                      } else if (isNaN(numVal) || numVal < 1 || numVal > 20) {
                        setMinJobLevelError(
                          t("Please enter a number between 1 and 20")
                        );
                      } else {
                        setMinJobLevelError("");
                        setMinJobLevelFilter(val);
                      }
                    }}
                  />
                </div>
                {minJobLevelError && (
                  <p className="text-red-600 text-sm mt-1">
                    {minJobLevelError}
                  </p>
                )}
              </div>

              <button
                className="mt-2 text-sm bg-[#961e38] text-white py-2 px-4 rounded"
                onClick={() => {
                  setTypeFilter("");
                  setLanguageFilter("");
                  setMinJobLevelFilter("");
                  setMinJobLevelOperator("=");
                }}
              >
                {t("Clear Filters")}
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="w-full h-full">
        <div className="block md:hidden w-full">
          {loading ? (
            <div className="flex items-center w-full justify-center mt-4 mb-4">
              <SpinnerLoader />
            </div>
          ) : courses?.length > 0 ? (
            courses.map((course: any, i: any) => {
              return (
                <div
                  key={i}
                  className="border border-[#c6c6c6] py-2 text-left bg-[#f3f3f3] mt-2"
                >
                  <div className="px-4 border-b border-[#c6c6c6] pb-2">
                    <h3 className="font-[600] pb-2"> {t("Course Name")}</h3>
                    <p className="text-lg"> {course.course_name}</p>
                  </div>
                  <div className="px-4 border-b border-[#c6c6c6] pb-2">
                    <h3 className="font-[600] pb-2">
                      {" "}
                      {t("Classroom Training Cost")}
                    </h3>
                    <input
                      className="w-[50px] border border-[#909090] text-center"
                      value={
                        editableFields[course.course_id]
                          ? newCosts[course.course_id]?.classroom
                          : course.classroom_training_cost
                      }
                      onChange={(e) =>
                        handleInputChange(
                          course.course_id,
                          "classroom",
                          e.target.value,
                          course.classroom_training_cost_id
                        )
                      }
                      disabled={!editableFields[course.course_id]}
                    />
                  </div>
                  <div className="px-4 border-b border-[#c6c6c6] pb-2">
                    <h3 className="font-[600] pb-2">
                      {" "}
                      {t("Online Training Cost")}
                    </h3>
                    <input
                      className="w-[50px] border border-[#909090] text-center"
                      value={
                        editableFields[course.course_id]
                          ? newCosts[course.course_id]?.online
                          : course.online_training_cost
                      }
                      disabled={!editableFields[course.course_id]}
                      onChange={(e) =>
                        handleInputChange(
                          course.course_id,
                          "online",
                          e.target.value,
                          course.online_training_cost_id
                        )
                      }
                    />
                  </div>
                  <div className="px-4 py-2">
                    {editableFields[course.course_id] ? (
                      <button
                        onClick={() => handleSave(course.course_id)}
                        className="text-green-500 cursor-pointer"
                      >
                        {t("Save")}
                      </button>
                    ) : (
                      <button
                        onClick={() => handleEdit(course.course_id)}
                        className="text-blue-500 cursor-pointer"
                      >
                        {t("Edit")}
                      </button>
                    )}
                  </div>
                </div>
              );
            })
          ) : (
            <p className="w-full text-center py-5">{t("No Data Available")}</p>
          )}
        </div>
        <div className="hidden md:block w-full h-full">
          <table className="w-full border-collapse">
            <thead className="bg-[#961e38] w-full text-white rounded-t-xl">
              <tr>
                <th className="py-2 px-4 text-left">{t("Course Name")}</th>
                <th className="py-2 px-2 text-center">
                  {t("Classroom Training Cost")}
                </th>
                <th className="py-2 px-2 text-center">
                  {t("Online Training Cost")}
                </th>
                <th className="py-2 px-2 text-center">{t("Actions")}</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={6} className="text-center py-5 mt-4 mb-4">
                    <SpinnerLoader />
                  </td>
                </tr>
              ) : (
                <>
                  {courses.length > 0 ? (
                    courses.map((course: any, i: any) => (
                      <tr key={i}>
                        <td className="border-b border-[#c6c6c6] py-2 px-4 text-left">
                          {course.course_name}
                        </td>
                        <td className="border-b border-[#c6c6c6] py-2 px-4 text-center">
                          <input
                            className="w-[50px] border border-[#909090] text-center"
                            value={
                              editableFields[course.course_id]
                                ? newCosts[course.course_id]?.classroom
                                : course.classroom_training_cost
                            }
                            onChange={(e) =>
                              handleInputChange(
                                course.course_id,
                                "classroom",
                                e.target.value,
                                course.classroom_training_cost_id
                              )
                            }
                            disabled={!editableFields[course.course_id]}
                          />
                        </td>
                        <td className="border-b border-[#c6c6c6] py-2 px-4 text-center">
                          <input
                            className="w-[50px] border border-[#909090] text-center"
                            value={
                              editableFields[course.course_id]
                                ? newCosts[course.course_id]?.online
                                : course.online_training_cost
                            }
                            onChange={(e) =>
                              handleInputChange(
                                course.course_id,
                                "online",
                                e.target.value,
                                course.online_training_cost_id
                              )
                            }
                            disabled={!editableFields[course.course_id]}
                          />
                        </td>
                        <td className="border-b border-[#c6c6c6] py-2 px-4 text-center">
                          {editableFields[course.course_id] ? (
                            <button
                              onClick={() => handleSave(course.course_id)}
                              className="text-green-500 cursor-pointer ml-8"
                            >
                              {t("Save")}
                            </button>
                          ) : (
                            <button
                              onClick={() => handleEdit(course.course_id)}
                              className="text-blue-500 cursor-pointer ml-8"
                            >
                              {t("Edit")}
                            </button>
                          )}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={2}>
                        <p className="w-full text-center py-5">
                          {t("No Data Available")}
                        </p>
                      </td>
                    </tr>
                  )}
                </>
              )}
            </tbody>
          </table>
        </div>

        {courses?.length > 0 && !loading && (
          <div className="flex flex-col md:flex-row justify-between items-center w-full gap-10">
            <div className="flex md:flex-row justify-center items-center gap-4 mt-4">
              {/* Previous Button */}
              <button
                className={`px-4 py-2 border rounded ${
                  currentPage === 1 ? "cursor-not-allowed opacity-50" : ""
                }`}
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                {t("Previous")}
              </button>

              {/* Dynamic Page Buttons */}
              {Array.from({
                length: Math.min(totalPages, isMobileOrTablet ? 2 : 4),
              }).map((_, index) => {
                const startPage = Math.max(
                  1,
                  Math.min(currentPage - 1, totalPages - 3)
                );
                const pageNumber = startPage + index;

                return (
                  <button
                    key={pageNumber}
                    className={`px-4 py-2 border rounded ${
                      currentPage === pageNumber
                        ? "bg-[#961e38] text-white"
                        : "bg-white text-[#961e38]"
                    }`}
                    onClick={() => handlePageChange(pageNumber)}
                  >
                    {pageNumber}
                  </button>
                );
              })}

              {/* Next Button */}
              <button
                className={`px-4 py-2 border rounded ${
                  currentPage === totalPages
                    ? "cursor-not-allowed opacity-50"
                    : ""
                }`}
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                {t("Next")}
              </button>
            </div>
            <div className="flex flex-col md:flex-row justify-center items-center gap-5 mt-4 md:mt-0">
              <p className="text-sm font-semibold">
                {t("Showing")}{" "}
                {itemsPerPage * (currentPage - 1) === 0
                  ? courseResp?.total_item_count > 0
                    ? 1
                    : 0
                  : itemsPerPage * (currentPage - 1) + 1}{" "}
                -{" "}
                {Math.min(
                  itemsPerPage * currentPage,
                  courseResp?.total_item_count || 0
                )}{" "}
                {t("of")} {courseResp?.total_item_count || 0} {t("results")}
              </p>
              <div className="flex items-center gap-3">
                <p className="text-sm font-semibold">{t("Items per page")}:</p>
                <select
                  value={itemsPerPage}
                  onChange={(e) => setItemsPerPage(Number(e.target.value))}
                  className="border rounded p-1"
                >
                  <option value={4}>4</option>
                  <option value={8}>8</option>
                  <option value={12}>12</option>
                </select>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default HRTrainingCost;
